import { cn } from '@/lib/utils/cn';

/**
 * Panneau de droite des écrans d'authentification.
 *
 * ⚠️ PROVISOIRE — la photographie provient d'Unsplash, le temps d'obtenir le
 * visuel définitif. Deux points à traiter avant une mise en production :
 *
 *  1. L'image est servie depuis `images.unsplash.com`, un domaine tiers. Cela
 *     ajoute une résolution DNS et une négociation TLS sur le chemin critique,
 *     et fait dépendre l'écran de connexion de la disponibilité d'Unsplash.
 *     Le visuel définitif devra être auto-hébergé dans `src/assets/images/`.
 *  2. Les conditions d'Unsplash imposent de créditer l'auteur. Le crédit est
 *     affiché en bas à droite ; il disparaîtra avec la photo.
 *
 * Le dégradé bleu profond par-dessus l'image n'est pas décoratif : sans lui,
 * le texte blanc deviendrait illisible sur les zones claires de la photo, et
 * l'ensemble sortirait de la charte.
 *
 * `NetworkGraphic` (composition réseau en SVG) reste disponible dans ce dossier
 * si vous souhaitez y revenir plutôt qu'utiliser une photographie.
 *
 * Entièrement décoratif : `aria-hidden` sur l'ensemble, `alt=""` sur l'image.
 * Le formulaire doit rester le seul parcours pour un lecteur d'écran, sinon
 * l'utilisateur traverse des libellés sans rapport avant le champ e-mail.
 *
 * Masqué sous `lg` : sur mobile, la colonne du formulaire occupe tout l'écran
 * et l'image n'est alors même pas téléchargée.
 */

/** Photo Unsplash vérifiée (HTTP 200, ~166 Ko à cette taille). */
const PHOTO_URL =
  'https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1400&q=70';

const PHOTO_CREDIT = { author: 'Dylan Gillis', platform: 'Unsplash' };

export const AuthShowcase = ({ className }: { className?: string }) => (
  <aside
    aria-hidden
    // Le fond bleu profond est posé sur le conteneur, pas seulement derrière
    // l'image : si Unsplash est injoignable, le panneau reste dans la charte
    // au lieu d'afficher un rectangle blanc.
    className={cn(
      'bg-navy-900 relative overflow-hidden rounded-[10px] shadow-[0_8px_24px_-12px_rgb(15_42_95/0.3)] ring-1 ring-navy-900/10',
      className,
    )}
  >
    <img
      src={PHOTO_URL}
      alt=""
      fetchPriority="high"
      draggable={false}
      className="absolute inset-0 size-full object-cover"
    />

    {/* Voile bleu profond. Deux couches superposées :
        — un aplat coloré en `mix-blend-multiply`, qui teinte la photo sans
          l'assombrir uniformément, en préservant ses contrastes ;
        — un dégradé vertical opaque en bas, qui garantit la lisibilité du
          texte quelle que soit la photo qui viendra la remplacer. */}
    <div className="bg-navy-900/70 absolute inset-0 mix-blend-multiply" />
    <div className="from-navy-900 via-navy-900/40 absolute inset-0 bg-linear-to-t to-transparent" />

    {/* Trame de points : elle casse la planéité du voile et raccroche le
        panneau au langage graphique de la marque. Masquée sur les bords, sinon
        elle se coupe net contre le bord du panneau. */}
    <div
      className="absolute inset-0 opacity-40"
      style={{
        backgroundImage: 'radial-gradient(circle, rgb(255 255 255 / 0.12) 1px, transparent 1px)',
        backgroundSize: '26px 26px',
        maskImage: 'radial-gradient(ellipse 80% 70% at 50% 40%, #000 30%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 40%, #000 30%, transparent 100%)',
      }}
    />

    <div className="absolute right-14 bottom-14 left-14">
      <span className="bg-accent-400 mb-5 block h-0.5 w-8" />
      <p className="font-heading max-w-md text-[26px] leading-[1.25] font-bold tracking-tight text-white">
        Une plateforme unique pour connecter vos talents.
      </p>
      <p className="mt-3 text-[15px] text-white/60">
        Des talents. Des opportunités. Un impact durable.
      </p>
    </div>

    {/* Crédit photo — exigé par les conditions d'utilisation d'Unsplash.
        Volontairement discret : il disparaîtra avec la photo provisoire. */}
    <p className="absolute right-4 bottom-4 text-[11px] text-white/35">
      Photo : {PHOTO_CREDIT.author} / {PHOTO_CREDIT.platform}
    </p>
  </aside>
);
